import {
  AssetCondition,
  AssetStatus,
  CurrencyCodeEnum,
  ProductTypeName,
} from '@electricjs/core_entity-client';
import { type Address } from '@electricjs/arc';
import { TFunction } from 'i18next';

import i18n from '@/i18n';
import { getTranslatedAssetStatusLabel } from '@/pages/Assets/AssetStatus';
import {
  Location,
  ShipmentEligibleAsset,
  Site,
  SiteWithAddress,
} from '@/types/assets';
import { ProductType } from '@/types/products';

import getProductTypeLabel from './getProductTypeLabel';
import { getFullName } from 'common/utils/getFullName';

export type ProductTypeOption = {
  label: string;
  value: string;
  name: ProductTypeName;
};

export type SiteOption = {
  label: string;
  value: string;
  name: string;
  isDisabled?: boolean;
  disabledTooltip?: string;
};

export enum ShippingOption {
  ADDRESS = 'address',
  SITE = 'site',
  WAREHOUSE = 'warehouse',
}

export type LocationOption = {
  label: string;
  value: string;
  name: string;
};

export type AssetConditionOption = {
  label: string;
  value: AssetCondition;
  name: string;
};

export type AssetStatusOption = {
  label: string;
  value: AssetStatus;
  name: string;
};

// Option shape for shipment-candidate asset search (Ship a Laptop asset selector).
// Carries enough raw asset data to prefill a shipment form once an asset is selected.
export type AssetOption = {
  label: string;
  value: string;
  name: string;
  serialNumber: string;
  assignedToEmployeeName?: string;
  assignedToEmployeeEmail?: string;
  assignedToEmployeePhone?: string;
  assignedToEmployeeAddress?: Address;
  site: SiteWithAddress | null;
};
export type CurrencyCodeOption = {
  label: string;
  value: CurrencyCodeEnum;
  name: string;
};

export const getAssetConditionDisplayNameMap = (
  t: TFunction
): Record<AssetCondition, string> => ({
  [AssetCondition.New]: t('assets:tableFilters.assetConditions.new'),
  [AssetCondition.UsedGood]: t('assets:tableFilters.assetConditions.usedGood'),
  [AssetCondition.UsedFair]: t('assets:tableFilters.assetConditions.usedFair'),
  [AssetCondition.UsedPoor]: t('assets:tableFilters.assetConditions.usedPoor'),
  [AssetCondition.Damaged]: t('assets:tableFilters.assetConditions.damaged'),
});

export const transformProductTypesToOptions = (
  productTypes: ProductType[]
): ProductTypeOption[] => {
  return productTypes.map((productType: ProductType) => ({
    value: productType.id,
    label: getProductTypeLabel(productType.name),
    name: productType.name,
  }));
};

export const transformSitesToOptions = (
  sites: Site[],
  options?: {
    disabledPredicate?: (site: Site) => boolean;
    disabledTooltip?: string;
  }
): SiteOption[] => {
  return sites.map((site: Site) => {
    const isDisabled = options?.disabledPredicate?.(site) ?? false;
    return {
      value: site.id,
      label: site.name,
      name: site.name,
      ...(isDisabled && {
        isDisabled: true,
        disabledTooltip: options?.disabledTooltip,
      }),
    };
  });
};

export const transformAssetsToOptions = (
  assets: ShipmentEligibleAsset[]
): AssetOption[] => {
  return assets.map((asset: ShipmentEligibleAsset) => {
    const assignedToEmployeeName = asset.assignedToEmployee
      ? getFullName(
          asset.assignedToEmployee.firstName,
          asset.assignedToEmployee.lastName
        )
      : undefined;

    return {
      value: asset.id,
      label: asset.serialNumber
        ? `${asset.name} - ${asset.serialNumber}`
        : asset.name,
      name: asset.name,
      serialNumber: asset.serialNumber,
      assignedToEmployeeName,
      assignedToEmployeeEmail: asset.assignedToEmployee?.email,
      assignedToEmployeePhone: asset.assignedToEmployee?.phoneNumber,
      assignedToEmployeeAddress: asset.assignedToEmployee?.address,
      site: asset.site,
    };
  });
};

export const transformLocationsToOptions = (
  locations: Location[]
): LocationOption[] => {
  return locations.map((location: Location) => ({
    value: location.id,
    label: location.name,
    name: location.name,
  }));
};
export const transformAssetConditionsToOptions = (
  t: TFunction
): AssetConditionOption[] => {
  const assetConditionDisplayNameMap = getAssetConditionDisplayNameMap(t);
  return Object.values(AssetCondition).map(condition => ({
    value: condition,
    label: assetConditionDisplayNameMap[condition] || condition,
    name: assetConditionDisplayNameMap[condition] || condition,
  }));
};

// Statuses set automatically by a backend processor (e.g. asset-shipment
// tracking) rather than by a user. They must never be manually assignable,
// and an asset currently in one of these statuses must not be editable.
// Append future warehouse-pipeline-style statuses here.
export const SYSTEM_MANAGED_ASSET_STATUSES: Set<AssetStatus> = new Set([
  AssetStatus.WarehouseIntake,
]);

export const getAssetStatusOption = (
  status: AssetStatus
): AssetStatusOption => {
  const label = getTranslatedAssetStatusLabel(status, i18n.t);
  return { value: status, label, name: label };
};

export const getAssetStatusOptions = (): AssetStatusOption[] => {
  return Object.values(AssetStatus)
    .filter(status => !SYSTEM_MANAGED_ASSET_STATUSES.has(status))
    .map(getAssetStatusOption);
};

export const getCurrencyCodeOptions = (): CurrencyCodeOption[] => {
  return Object.values(CurrencyCodeEnum).map(code => ({
    value: code,
    label: code,
    name: code,
  }));
};
